import React, { useState, useRef, useEffect } from 'react';
import Select, { components } from 'react-select';
import ReactDOM from 'react-dom';
import services from '../../services';

const statusColors = [
    '#3b82f6',
    '#60a5fa',
    '#38bdf8',
    '#22d3ee',
    '#14b8a6',
    '#22c55e',
    '#a3e635',
    '#eab308',
    '#facc15',
    '#f59e0b',
    '#f97316',
    '#ef4444',
    '#ec4899',
    '#8b5cf6',
    '#a855f7',
    '#d1d5db',
    '#6b7280',
    '#374151',
    '#000000',
];

const getContrastColor = (hexColor = '#2196f3') => {
    const hex = hexColor.replace('#', '');
    const r = parseInt(hex.substring(0, 2), 16);
    const g = parseInt(hex.substring(2, 4), 16);
    const b = parseInt(hex.substring(4, 6), 16);
    const brightness = (r * 299 + g * 587 + b * 114) / 1000;
    return brightness > 128 ? '#000' : '#fff';
};

const customStyles = {
    control: (base) => ({
        ...base,
        minHeight: '36px',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        boxShadow: 'none',
        backgroundColor: '#ffffff',
        '&:hover': {
            borderColor: '#cbd5e1',
        },
        '&:focus-within': {
            borderColor: '#3b82f6',
            boxShadow: '0 0 0 1px #3b82f6',
        },
    }),
    placeholder: (base) => ({
        ...base,
        color: '#94a3b8',
        fontSize: '14px',
    }),
    input: (base) => ({
        ...base,
        fontSize: '14px',
        color: '#1e293b',
    }),
    valueContainer: (base) => ({
        ...base,
        padding: '4px 8px',
    }),
    multiValue: (base, { data }) => {
        const color = data.color || '#2196f3';
        return {
            ...base,
            backgroundColor: 'transparent',
            border: `1px solid ${color}`,
            borderRadius: '4px',
            margin: '2px',
        };
    },
    multiValueLabel: (base, { data }) => ({
        ...base,
        color: data.color || '#2196f3',
        fontSize: '12px',
        fontWeight: '500',
        padding: '2px 6px',
    }),
    multiValueRemove: (base, { data }) => ({
        ...base,
        color: data.color || '#2196f3',
        ':hover': {
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
            color: data.color || '#2196f3',
        },
    }),
    option: (base, { isFocused }) => ({
        ...base,
        backgroundColor: isFocused ? '#f1f5f9' : 'transparent',
        color: '#1e293b',
        cursor: 'pointer',
        padding: '8px 12px',
        fontSize: '14px',
        border: 'none',
        ':active': {
            backgroundColor: '#e2e8f0',
        },
    }),
    menu: (base) => ({
        ...base,
        margin: '0',
        backgroundColor: '#ffffff',
        zIndex: 9999,
    }),
    menuList: (base) => ({
        ...base,
        padding: '4px 0',
    }),
    indicatorsContainer: (base) => ({
        ...base,
        paddingRight: '8px',
    }),
    indicatorSeparator: (base) => ({
        ...base,
        display: 'none',
    }),
    dropdownIndicator: (base) => ({
        ...base,
        color: '#64748b',
        ':hover': {
            color: '#475569',
        },
    }),
};

const normalizeTagValue = (val) => {
    if (typeof val === "string") return val;
    if (typeof val === "number") return String(val);
    if (typeof val === "object" && val !== null) {
        return val.label || val.name || val.value || JSON.stringify(val);
    }
    return "";
};

const generateFilteredOptions = (tagFieldsData, input = '') => {
    const rawTags =
        tagFieldsData?.flatMap((tag) => {
            const rawValue = tag?.customField?.value;

            const values = Array.isArray(rawValue)
                ? rawValue
                : rawValue !== undefined && rawValue !== null
                    ? [rawValue]
                    : [];

            return values.map((val) => {
                const label = normalizeTagValue(val);

                return {
                    label,
                    value: label,
                    color: tag?.customField?.color || '#2196f3',
                    tagId: tag?.id,
                    id: tag?.id,
                    index: tag?.index ?? 0,
                };
            });
        }) ?? [];

    const map = new Map();
    rawTags.forEach((t) => {
        if (!map.has(t.label)) map.set(t.label, t);
    });

    const safeInput = String(input).toLowerCase();

    return [...map.values()]
        .filter((tag) => tag.label.toLowerCase().includes(safeInput))
        .sort((a, b) => (a.index ?? 0) - (b.index ?? 0));
};

const CustomTagSelect = ({
    openStatusIndex,
    tagFieldsData,
    handleStatusClick,
    item,
    handleSelecteTag,
    setSelectedFilterTagVal,
    selectedFilterTagVal,
    setSelectedTagColor,
    customeFiedId,
    setCustomeFieldId,
    deleteCustomField,
    editTagName,
    setEditTagName,
    taskItem,
    selectedTags,
    selectedFieldId,
    idx,
    handletaskUpdate,
    customprokey,
    setCustomprokey,
    triggerTaskUpdate,
    setTriggerTaskUpdate,
    updateCustomProKey,
    selecteTagItem,
    formattedTag,
    handleUpdatedCustomField,
    handleRemoveCustomField,
    setOpenStatusIndex,
    getAllPropertyByTaskId,
    getPropertyByID,
    getAllProperty
}) => {
    const [inputValue, setInputValue] = useState('');
    const [menuIsOpen, setMenuIsOpen] = useState(true);
    const [currentSelectedColor, setCurrentSelectedColor] = useState('#2196f3');
    const [isDragging, setIsDragging] = useState(false);
    const [dragIndex, setDragIndex] = useState(null);
    const [hoveredId, setHoveredId] = useState(null);
    const [showOptionModal, setShowOptionModal] = useState(null);
    const [modalPosition, setModalPosition] = useState({ top: 0, left: 0 });
    const [inputTagEditValue, setInputTagEditValue] = useState('');
    const [selectedEditTagColor, setSelectedEditTagColor] = useState('#2196f3');
    const [selecteTagIndex, setSelecteTagIndex] = useState(null);
    const modalRef = useRef(null);

    // Generate tag options from tagFieldsData
    const [tagOptions, setTagOptions] = useState([]);

    useEffect(() => {
        const options = generateFilteredOptions(tagFieldsData, '');
        setTagOptions(options);
    }, [tagFieldsData]);

    // Filter options based on input
    const filteredOptions = tagOptions.filter(opt =>
        opt.label.toLowerCase().includes(inputValue.toLowerCase())
    );

    const masteroptionsData = tagOptions;

    // Get selected values
    function filterObjectsByIds(idArray, objectArray) {
        return objectArray.filter(obj => idArray.includes(obj.id));
    }

    const formattedTagNew = filterObjectsByIds(selectedFieldId, masteroptionsData);

    const formatted = formattedTagNew.map((item) => ({
        label: `${item.label}`,
        value: item.value,
        color: item.color || '#2196f3',
        id: item?.id
    }));

    // Handle click outside modal
    useEffect(() => {
        const handleMouseDownOutside = (event) => {
            if (modalRef.current && !modalRef.current.contains(event.target)) {
                setShowOptionModal(null);
            }
        };
        document.addEventListener('mousedown', handleMouseDownOutside);
        return () => {
            document.removeEventListener('mousedown', handleMouseDownOutside);
        };
    }, []);

    // Draggable Menu List component (same pattern as StatusDropdown)
    const DraggableMenuList = (props) => {
        const { selectProps } = props;
        const isDraggingRef = useRef(false);

        const handleDrop = async (event, dropIndex) => {
            event.preventDefault();
            if (dragIndex === null || dragIndex === dropIndex) return;

            const draggedOption = tagOptions[dragIndex];

            console.log('Drag reorder:', {
                draggedItem: draggedOption?.label,
                fromPosition: dragIndex,
                toPosition: dropIndex,
                newIndex: dropIndex,
                draggedOptionId: draggedOption?.id,
                color: draggedOption?.color
            });

            // API call with color preserved
            await services.customfield.UPDATE_CUSTOM_FIELD(draggedOption.id, {
                customPropertyId: item.id,
                index: dropIndex,
                color: draggedOption?.color,
            });

            // Reorder options locally
            const newTagOptions = [...tagOptions];
            newTagOptions.splice(dragIndex, 1);
            newTagOptions.splice(dropIndex, 0, draggedOption);

            setTagOptions(
                newTagOptions.map((opt, i) => ({ ...opt, index: i }))
            );

            setDragIndex(null);
            isDraggingRef.current = false;
            selectProps.setIsDragging(false);

            // Refresh data from API
            await getAllProperty();
            // await getPropertyByID(item?.boardId);
            await getAllPropertyByTaskId(item?.id, 'Tag');
        };

        return (
            <components.MenuList {...props}>
                {tagOptions.map((opt, index) => (
                    <div
                        onMouseDown={(e) => e.stopPropagation()}
                        key={opt.id}
                        draggable
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => handleDrop(e, index)}
                        onDragStart={(e) => {
                            e.stopPropagation();
                            selectProps.setIsDragging(true);
                            setDragIndex(index);
                            isDraggingRef.current = true;
                            e.dataTransfer.effectAllowed = "move";
                        }}
                        onDragEnd={() => {
                            setDragIndex(null);
                            isDraggingRef.current = false;
                            selectProps.setIsDragging(false);
                        }}
                        onClick={() => {
                            if (!isDraggingRef.current) {
                                // Handle tag selection
                                const isAlreadySelected = formatted.some(f => f.id === opt.id);
                                if (!isAlreadySelected) {
                                    updateCustomProKey(item, opt.id, [...formatted, opt]);
                                }
                            }
                        }}
                        onMouseEnter={() => setHoveredId(opt.id)}
                        onMouseLeave={() => setHoveredId(null)}
                        style={{
                            padding: "8px 12px",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            background: "#fff",
                            borderBottom: "1px solid #eee",
                            cursor: "grab",
                        }}
                    >
                        {/* TAG LABEL */}
                        <span
                            style={{
                                padding: "4px 8px",
                                borderRadius: 4,
                                border: `1px solid ${opt.color}`,
                                color: opt.color,
                                fontSize: 12,
                            }}
                        >
                            #{opt.label}
                        </span>

                        {/* ACTIONS */}
                        <div className="flex items-center gap-2">
                            {hoveredId === opt.id && (
                                <i
                                    className="bi bi-sliders text-gray-500 cursor-pointer"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        const rect = e.currentTarget.getBoundingClientRect();
                                        setModalPosition({
                                            top: rect.bottom + window.scrollY,
                                            left: rect.left + window.scrollX,
                                        });
                                        setShowOptionModal(opt.id);
                                        setInputTagEditValue(opt.label);
                                        setCustomeFieldId(opt.id);
                                        setSelecteTagIndex(opt.index);
                                        setSelectedEditTagColor(opt.color);
                                    }}
                                />
                            )}

                            {/* DRAG HANDLE */}
                            <span
                                onMouseDown={(e) => e.stopPropagation()}
                                style={{
                                    width: 16,
                                    cursor: "grab",
                                    fontSize: 14,
                                    color: "#999",
                                }}
                            >
                                ⋮⋮
                            </span>
                        </div>
                    </div>
                ))}
            </components.MenuList>
        );
    };

    const handleChange = async (newSelected, actionMeta) => {
        setSelectedFilterTagVal(newSelected);

        const taskId = taskItem?.id;
        const propertyId = item?.id;
        if (!taskId || !propertyId) return;

        if (actionMeta.action === 'select-option') {
            const lastSelected = actionMeta.option;
            const tagValId = lastSelected?.id;
            await updateCustomProKey(item, tagValId, newSelected);
        }
        if (actionMeta.action === 'remove-value') {
            const removed = actionMeta.removedValue;
            const tagValId = removed?.id;
            await updateCustomProKey(item, tagValId, newSelected, 'remove');
        }
    };

    const handleKeyDown = (event) => {
        if (event.key === 'Enter' && inputValue.trim()) {
            event.preventDefault();

            const isHexColor = /^#[0-9A-Fa-f]{6}$/.test(inputValue.trim());
            let tagLabel, tagColor;

            if (isHexColor) {
                tagLabel = `Color ${inputValue.trim()}`;
                tagColor = inputValue.trim();
            } else {
                tagLabel = inputValue.trim();
                tagColor = currentSelectedColor;
            }

            if (handleSelecteTag) {
                handleSelecteTag(item, inputValue);
            }

            setInputValue('');
        }
    };

    return (
        <div className="relative z-[999]">
            <Select
                className="fs-[13px]"
                style={{ border: '0px' }}
                key={idx}
                isMulti
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                components={{ MenuList: DraggableMenuList }}
                inputValue={inputValue}
                styles={customStyles}
                value={formatted}
                onChange={handleChange}
                onInputChange={(value, { action }) => {
                    if (action === 'input-change') {
                        setInputValue(value);
                    }
                }}
                onKeyDown={handleKeyDown}
                options={filteredOptions}
                placeholder={`Type to create or select an option`}
                noOptionsMessage={() => 'No tags found'}
                isClearable={false}
                menuIsOpen={menuIsOpen || isDragging}
                onMenuOpen={() => setMenuIsOpen(true)}
                onMenuClose={() => {
                    // Don't close menu while dragging
                    if (!isDragging) {
                        setMenuIsOpen(false);
                    }
                }}
                onBlur={(e) => {
                    // Prevent blur from closing menu while dragging
                    if (isDragging) {
                        e.preventDefault();
                    }
                }}
                blurInputOnSelect={false}
                menuShouldScrollIntoView={false}
                setIsDragging={setIsDragging}
            />

            {/* OPTION DETAILS MODAL (outside Select, same as StatusDropdown, using Portal to stay on top) */}
            {tagOptions.map((tag) => {
                const isModalOpen = showOptionModal === tag.id;
                if (!isModalOpen) return null;
                return ReactDOM.createPortal(
                    <div
                        key={tag.id}
                        ref={modalRef}
                        className="fixed bg-white border border-gray-300 shadow-lg rounded p-4"
                        style={{
                            top: `${modalPosition.top}px`,
                            left: `${modalPosition.left}px`,
                            transform: "translate(-80%, 10px)",
                            zIndex: 10001,
                            width: '260px'
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        <div className="flex justify-between items-center mb-2">
                            <h3 className="text-sm font-semibold">Option details</h3>
                            <i
                                className="bi bi-trash text-black-500 cursor-pointer"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    deleteCustomField(customeFiedId, '', item);
                                    setShowOptionModal(null);
                                }}
                            />
                        </div>

                        <input
                            type="text"
                            className="w-full border border-black rounded px-2 py-1 text-sm mb-3"
                            value={inputTagEditValue}
                            onChange={(e) => setInputTagEditValue(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                        />

                        <div className="text-sm font-medium mb-2 text-black">Colors</div>

                        <div className="grid grid-cols-5 gap-2">
                            {statusColors.map((color) => (
                                <div
                                    key={color}
                                    className="w-6 h-6 rounded-full cursor-pointer relative flex items-center justify-center"
                                    style={{ backgroundColor: color }}
                                    onClick={() => setSelectedEditTagColor(color)}
                                >
                                    {selectedEditTagColor === color && (
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="w-4 h-4 text-white"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                            strokeWidth={3}
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                                        </svg>
                                    )}
                                </div>
                            ))}
                        </div>

                        <div className="flex justify-end gap-2 mt-3">
                            <button
                                className="text-xs text-white px-3 py-1.5 rounded-md hover:bg-blue-600 transition-colors"
                                onClick={() => {
                                    setShowOptionModal(null);
                                    handleSelecteTag(item, inputTagEditValue, selectedEditTagColor, selecteTagIndex, true);
                                }}
                                style={{ backgroundColor: 'rgb(22, 96, 200)' }}
                            >
                                Save
                            </button>
                            <button
                                className="text-xs bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-300 transition-colors"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    e.preventDefault();
                                    setShowOptionModal(null);
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>,
                    document.body
                );
            })}
        </div>
    );
};

export default CustomTagSelect;
